import React, { useRef, useState } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert, Divider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StoreIcon from '@mui/icons-material/Store';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AddInventoryItem from '../AddInventoryItem';
import type { StockCardPrefill } from '../stockCard/types';
import QuickAddItemModal from '../QuickAddItemModal';
import InventoryCategoryModal from '../InventoryCategoryModal';
import InventoryProductFeatureModal from '../InventoryProductFeatureModal';
import InventoryBrandModal from '../InventoryBrandModal';
import InventoryWarehouseModal from '../InventoryWarehouseModal';
import InventoryTableSettings from '../InventoryTableSettings';
import InventoryImportModal from '../InventoryImportModal';
import InventoryItemDetailModal from '../InventoryItemDetailModal';
import ImageLibrarySelectionModal from '../ImageLibrarySelectionModal';
import N11ProductUploadModal from '../N11ProductUploadModal';
import N11CategorySelectionModal from '../N11CategorySelectionModal';
import N11LogModal from '../N11LogModal';
import ShipmentTemplatesModal from '../ShipmentTemplatesModal';
import { SingleItemDeleteDialog } from '../dialogs/SingleItemDeleteDialog';
import { UpdateValueDialog } from '../dialogs/UpdateValueDialog';
import { BulkDeleteDialog } from '../dialogs/BulkDeleteDialog';
import { BulkInactivateDialog } from '../dialogs/BulkInactivateDialog';
import { BulkPriceUpdateDialog } from '../dialogs/BulkPriceUpdateDialog';
import { BulkDeleteImagesDialog } from '../dialogs/BulkDeleteImagesDialog';
import { BulkEcommerceDialog } from '../dialogs/BulkEcommerceDialog';
import { DeleteAllItemsDialog } from '../dialogs/DeleteAllItemsDialog';
import { DeleteAllProductsDialog } from '../dialogs/DeleteAllProductsDialog';
import type { DeleteAllPhase, DeleteAllProductsResult } from '../dialogs/DeleteAllProductsDialog';
import { BulkKioskSendDialog } from '../dialogs/BulkKioskSendDialog';
import { BulkQRMenuSendDialog } from '../dialogs/BulkQRMenuSendDialog';
import { BulkWarehouseAssignDialog } from '../dialogs/BulkWarehouseAssignDialog';
import { BulkVatRateUpdateDialog } from '../dialogs/BulkVatRateUpdateDialog';
import { BulkDepartmentNoUpdateDialog } from '../dialogs/BulkDepartmentNoUpdateDialog';
import { BulkVatAndDepartmentUpdateDialog } from '../dialogs/BulkVatAndDepartmentUpdateDialog';
import AddStockChoiceModal from './AddStockChoiceModal';
import CatalogPickerModal from './CatalogPickerModal';
import { BulkActionSelectionModal } from './BulkActionSelectionModal';
import { FeaturesMenu } from './FeaturesMenu';
import { OtherActionsModal } from './OtherActionsModal';
import type { InventoryItem, InventoryCategory, Brand, Warehouse, TableColumn } from '../types/inventory.types';
import { getCurrencySymbol } from '../utils/formatters';
import { useTranslation } from 'react-i18next';

interface InventoryModalsProps {
  // Modal States
  /** "Yeni Stok Ekle" tıklanınca çıkan yol ayrımı: sıfırdan ürün mü, katalogdan mı. */
  isAddChoiceModalOpen: boolean;
  isCatalogPickerOpen: boolean;
  isAddModalOpen: boolean;
  isEditModalOpen: boolean; // ✅ YENİ: Düzenleme modalı
  isQuickAddModalOpen: boolean;
  isCategoryModalOpen: boolean;
  isProductFeatureModalOpen: boolean;
  isBrandModalOpen: boolean;
  isWarehouseModalOpen: boolean;
  isTableSettingsModalOpen: boolean;
  isImportModalOpen: boolean;
  deleteDialogOpen: boolean;
  isUpdateModalOpen: boolean;
  isDetailModalOpen: boolean;
  isImageLibraryModalOpen: boolean;
  isN11UploadModalOpen: boolean;
  isN11CategoryModalOpen: boolean;
  isN11LogModalOpen: boolean;
  isShipmentTemplatesModalOpen: boolean;
  isBulkActionSelectionModalOpen: boolean;
  isOtherActionsModalOpen: boolean;
  showDeleteAllDialog: boolean;
  showDeleteAllProductsDialog: boolean;
  snackbarOpen: boolean;

  // Modal Handlers
  onCloseAddChoiceModal: () => void;
  onChooseNewProduct: () => void;
  onChooseFromCatalog: () => void;
  onCloseCatalogPicker: () => void;
  onCatalogTransferred: () => void;
  onCloseAddModal: () => void;
  onCloseEditModal: () => void; // ✅ YENİ: Düzenleme modalı kapama
  onCloseQuickAddModal: () => void;
  onCloseCategoryModal: () => void;
  onCloseProductFeatureModal: () => void;
  onCloseBrandModal: () => void;
  onCloseWarehouseModal: () => void;
  onCloseTableSettingsModal: () => void;
  onCloseImportModal: () => void;
  onCloseDeleteDialog: () => void;
  onCloseUpdateModal: () => void;
  onCloseDetailModal: () => void;
  onCloseImageLibraryModal: () => void;
  onCloseN11UploadModal: () => void;
  onCloseN11CategoryModal: () => void;
  onCloseSnackbar: () => void;

  // Data
  selectedItem: InventoryItem | null;
  selectedImageItem: InventoryItem | null;
  selectedItemForN11: InventoryItem | null;
  selectedField: 'quantity' | 'salePrice' | 'vatRate' | 'departmentNo' | 'name' | null;
  selectedBranch: any;
  selectedWarehouse: Warehouse | null;
  categories: InventoryCategory[];
  brands: Brand[];
  warehouses: Warehouse[];
  tableColumns: TableColumn[];
  companyId: string | null;
  inventoryItems: InventoryItem[];

  // Values
  updateValue: number;
  updateStringValue: string;
  updateListPrice: number;
  isUpdating: boolean;
  isDeleting: boolean;
  syncWithN11: boolean;
  n11IntegrationActive: boolean;
  isN11Syncing: boolean;
  n11SendingProduct: boolean;
  snackbarMessage: string;
  snackbarSeverity: 'success' | 'error' | 'warning' | 'info';
  deletingAll: boolean;
  deleteProgress: number;
  deletedItemsCount: number;
  totalItemsToDelete: number;

  // Tüm ürünleri kalıcı silme (hard delete)
  deleteAllCounting: boolean;
  deleteAllProductCount: number;
  deleteAllCategoryCount: number;
  deleteAllDeleting: boolean;
  deleteAllPhase: DeleteAllPhase | null;
  deleteAllProcessed: number;
  deleteAllPhaseTotal: number;
  deleteAllResult: DeleteAllProductsResult | null;
  onCloseDeleteAllProducts: () => void;
  onConfirmDeleteAllProducts: (includeCategories: boolean) => void;
  refreshing: boolean;
  loading: boolean;

  // Callbacks
  onUpdateValueChange: (value: number) => void;
  onUpdateStringValueChange: (value: string) => void;
  onUpdateListPriceChange: (value: number) => void;
  onSyncWithN11Change: (value: boolean) => void;
  onConfirmUpdate: (movementData?: any) => Promise<void>;
  onDeleteItem: () => Promise<void>;
  onUpdateTableSettings: (columns: TableColumn[]) => void;
  onItemUpdate: (item: InventoryItem) => void;
  onSaveImages: (urls: string[]) => Promise<void>;
  onSaveN11Log: (logEntry: any) => void;
  onN11CategoryConfirm: (categoryId: string, shipmentTemplate?: string) => Promise<void>;
  onConfirmDeleteAll: () => Promise<void>;
  onCancelDeleteAll: () => void;

  // Bulk Operations
  bulkOps: any;
  selectedItems: string[];
  onBulkDeleteConfirm: () => Promise<void>;
  onBulkDeleteCancel: () => void;
  onBulkInactivateConfirm: () => Promise<void>;
  onBulkInactivateCancel: () => void;
  onBulkPriceUpdateConfirm: () => Promise<void>;
  onBulkPriceUpdateCancel: () => void;
  onBulkDeleteImagesConfirm: () => Promise<void>;
  onBulkDeleteImagesCancel: () => void;
  onBulkEcommerceConfirm: () => Promise<void>;
  onBulkEcommerceCancel: () => void;
  onBulkToggleQRMenuConfirm: () => Promise<void>;
  onBulkToggleQRMenuCancel: () => void;
  onBulkToggleKioskConfirm: () => Promise<void>;
  onBulkToggleKioskCancel: () => void;
  onBulkAssignWarehouseConfirm: (warehouseId: string) => Promise<void>;
  onBulkAssignWarehouseCancel: () => void;
  onBulkUpdateVatRateConfirm: () => Promise<void>;
  onBulkUpdateVatRateCancel: () => void;
  onBulkUpdateDepartmentNoConfirm: () => Promise<void>;
  onBulkUpdateDepartmentNoCancel: () => void;
  onBulkUpdateVatAndDeptConfirm: () => Promise<void>;
  onBulkUpdateVatAndDeptCancel: () => void;
  onSelectBulkAction: (action: string) => void;
  onCloseBulkActionModal: () => void;

  // Menu
  menuAnchorEl: HTMLElement | null;
  menuItemId: string | null;
  ecommerceStatuses: {[key: string]: boolean};
  onMenuClose: () => void;
  onMenuDetails: () => void; // ✅ YENİ: Stok Ayrıntıları
  onMenuStockMovement: () => void; // ✅ YENİ: Stok Hareketi
  onMenuStockMovementsReport: () => void; // ✅ YENİ: Hareket Raporu
  onMenuEdit: () => void;
  onMenuDelete: () => void;
  onOpenForEcommerce: () => void;
  onSendToN11: () => void;
  onToggleQRMenu: () => void;
  onToggleKiosk: () => void;
  onActivateItem: () => void;
  onOpenBranchPrice: () => void; // ✅ YENİ: Şube Fiyatı
  onOpenRecipeModal: () => void; // ✅ YENİ: Basit Üretim Reçetesi
  onOpenMenuModal: () => void; // ✅ YENİ: Menü Tanımları
  onOpenVariantModal: () => void; // ✅ YENİ: Varyantlari Yonet

  // Features Menu
  anchorElFeatures: HTMLElement | null;
  onCloseFeaturesMenu: () => void;
  bulkActionMode: boolean;
  loadingExport: boolean;
  isMobile: boolean;
  onOpenCategoryModal: () => void;
  onOpenProductFeatureModal: () => void;
  onOpenBrandModal: () => void;
  onOpenWarehouseModal: () => void;
  onStockMovementsReport: () => void;
  onToggleBulkActionMode: () => void;
  onNavigateToImageLibrary: () => void;
  onOpenTableSettings: () => void;
  onOpenShipmentTemplates: () => void;
  onExportToExcel: () => Promise<void>;
  onOpenImportModal: () => void;
  onOpenN11Upload: () => void;
  onOpenN11Log: () => void;
  onOpenOtherActionsModal: () => void;
  onPriceChangeLogs: () => void;
  onLabelPrinting: () => void;
  onScaleSync: () => void;
  onProductRecipesPage: () => void;
  onProductMenusPage: () => void;
  onStockCountPage?: () => void;

  // Other Actions Modal
  onNavigateToImageLibraryFromOther: () => void;
  onOpenTableSettingsFromOther: () => void;
  onOpenShipmentTemplatesFromOther: () => void;
  onOpenN11LogFromOther: () => void;
  onOpenN11UploadFromOther: () => void;
  onRefreshInventory: () => Promise<void>;
  onCloseOtherActionsModal: () => void;
}

export const InventoryModals: React.FC<InventoryModalsProps> = (props) => {
  // Hızlı ekle -> "Tam karta geç": girilen değerlerle yeni stok kartı açılır.
  const [fullCardPrefill, setFullCardPrefill] = useState<StockCardPrefill | null>(null);
  const { t } = useTranslation('accounting');
  const [platformMenuAnchorEl, setPlatformMenuAnchorEl] = useState<null | HTMLElement>(null);
  const platformMenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedMenuItem = props.menuItemId
    ? props.inventoryItems.find(item => item.id === props.menuItemId)
    : null;
  const canManageVariants = selectedMenuItem?.isVariantParent === true;

  const handleOpenPlatformMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (platformMenuCloseTimeoutRef.current) {
      clearTimeout(platformMenuCloseTimeoutRef.current);
      platformMenuCloseTimeoutRef.current = null;
    }
    setPlatformMenuAnchorEl(event.currentTarget);
  };

  const handleClosePlatformMenu = () => {
    setPlatformMenuAnchorEl(null);
  };

  const handleClosePlatformMenuWithDelay = () => {
    if (platformMenuCloseTimeoutRef.current) {
      clearTimeout(platformMenuCloseTimeoutRef.current);
    }
    platformMenuCloseTimeoutRef.current = setTimeout(() => {
      setPlatformMenuAnchorEl(null);
      platformMenuCloseTimeoutRef.current = null;
    }, 180);
  };

  const handleCloseAllMenus = () => {
    handleClosePlatformMenu();
    props.onMenuClose();
  };

  const handlePlatformAction = (action: () => void) => {
    handleClosePlatformMenu();
    action();
  };

  return (
    <>
      {/* Add/Edit Modals */}
      <AddStockChoiceModal
        open={props.isAddChoiceModalOpen}
        onClose={props.onCloseAddChoiceModal}
        onChooseNewProduct={props.onChooseNewProduct}
        onChooseFromCatalog={props.onChooseFromCatalog}
      />

      <CatalogPickerModal
        open={props.isCatalogPickerOpen}
        onClose={props.onCloseCatalogPicker}
        companyId={props.companyId}
        onTransferred={props.onCatalogTransferred}
      />

      <AddInventoryItem
        open={props.isAddModalOpen}
        onClose={props.onCloseAddModal}
      />
      
      {/* ✅ YENİ: Düzenleme Modalı */}
      <AddInventoryItem
        open={props.isEditModalOpen}
        onClose={props.onCloseEditModal}
        itemToEdit={props.selectedItem}
        onItemUpdated={props.onItemUpdate}
      />
      
      <QuickAddItemModal
        open={props.isQuickAddModalOpen}
        onClose={props.onCloseQuickAddModal}
        branchId={props.selectedBranch ? props.selectedBranch.id : undefined} 
        onOpenFullCard={(prefill) => setFullCardPrefill(prefill)}
      />

      <AddInventoryItem
        open={fullCardPrefill !== null}
        onClose={() => setFullCardPrefill(null)}
        prefill={fullCardPrefill ?? undefined}
        branchId={props.selectedBranch ? props.selectedBranch.id : undefined}
      />
      
      <InventoryCategoryModal
        open={props.isCategoryModalOpen}
        onClose={props.onCloseCategoryModal}
      />

      <InventoryProductFeatureModal
        open={props.isProductFeatureModalOpen}
        onClose={props.onCloseProductFeatureModal}
      />
      
      <InventoryBrandModal
        open={props.isBrandModalOpen}
        onClose={props.onCloseBrandModal}
      />
      
      <InventoryWarehouseModal
        open={props.isWarehouseModalOpen}
        onClose={props.onCloseWarehouseModal}
      />

      <InventoryTableSettings
        open={props.isTableSettingsModalOpen}
        onClose={props.onCloseTableSettingsModal}
        onSave={props.onUpdateTableSettings}
        currentColumns={props.tableColumns}
      />

      <InventoryImportModal
        open={props.isImportModalOpen}
        onClose={props.onCloseImportModal}
      />
      
      {/* Delete/Update Dialogs */}
      <SingleItemDeleteDialog
        open={props.deleteDialogOpen}
        onClose={props.onCloseDeleteDialog}
        onConfirm={props.onDeleteItem}
        item={props.selectedItem}
        deleting={props.isDeleting}
      />

      <UpdateValueDialog
        open={props.isUpdateModalOpen}
        onClose={props.onCloseUpdateModal}
        selectedItem={props.selectedItem}
        selectedField={props.selectedField}
        updateValue={props.updateValue}
        onUpdateValueChange={props.onUpdateValueChange}
        updateStringValue={props.updateStringValue}
        onUpdateStringValueChange={props.onUpdateStringValueChange}
        updateListPrice={props.updateListPrice}
        onUpdateListPriceChange={props.onUpdateListPriceChange}
        isUpdating={props.isUpdating}
        onConfirm={props.onConfirmUpdate}
        n11IntegrationActive={props.n11IntegrationActive}
        syncWithN11={props.syncWithN11}
        onSyncWithN11Change={props.onSyncWithN11Change}
        isN11Syncing={props.isN11Syncing}
      />

      {/* Detail Modals */}
      <InventoryItemDetailModal
        open={props.isDetailModalOpen}
        onClose={props.onCloseDetailModal}
        item={props.selectedItem}
        categories={props.categories}
        brands={props.brands}
        warehouses={props.warehouses}
        companyId={props.companyId}
        onItemUpdate={props.onItemUpdate}
      />

      <ImageLibrarySelectionModal
        open={props.isImageLibraryModalOpen}
        onClose={props.onCloseImageLibraryModal}
        onSave={props.onSaveImages}
        productId={props.selectedImageItem?.id || null}
        productName={props.selectedImageItem?.name || ''}
        currentImageUrls={props.selectedImageItem?.imageUrl?.split(',').map(u => u.trim()).filter(Boolean) || []}
        companyId={props.companyId}
      />

      {/* N11 Modals */}
      <N11ProductUploadModal
        open={props.isN11UploadModalOpen}
        onClose={props.onCloseN11UploadModal}
        companyId={props.companyId}
        onSaveLog={props.onSaveN11Log}
      />

      <N11CategorySelectionModal
        open={props.isN11CategoryModalOpen}
        onClose={props.onCloseN11CategoryModal}
        onConfirm={props.onN11CategoryConfirm}
        item={props.selectedItemForN11 as any} 
        companyId={props.companyId}
        loading={props.n11SendingProduct}
      />

      <N11LogModal
        open={props.isN11LogModalOpen}
        onClose={() => {}}
        companyId={props.companyId}
      />

      <ShipmentTemplatesModal
        open={props.isShipmentTemplatesModalOpen}
        onClose={() => {}}
        companyId={props.companyId}
      />

      {/* Bulk Operation Dialogs */}
      <BulkDeleteDialog
        open={props.bulkOps.showBulkDeleteDialog}
        loading={props.bulkOps.bulkDeleting}
        selectedCount={props.selectedItems.length}
        onConfirm={props.onBulkDeleteConfirm}
        onCancel={props.onBulkDeleteCancel}
      />

      <BulkInactivateDialog
        open={props.bulkOps.showBulkInactivateDialog}
        loading={props.bulkOps.bulkInactivating}
        selectedCount={props.selectedItems.length}
        onConfirm={props.onBulkInactivateConfirm}
        onCancel={props.onBulkInactivateCancel}
      />

      <BulkPriceUpdateDialog
        open={props.bulkOps.showBulkPriceUpdateDialog}
        loading={props.bulkOps.bulkPriceUpdating}
        selectedCount={props.selectedItems.length}
        priceUpdateType={props.bulkOps.priceUpdateType}
        priceUpdateValue={props.bulkOps.priceUpdateValue}
        priceUpdateTarget={props.bulkOps.priceUpdateTarget}
        currencySymbol={props.selectedItems.length > 0 ? 
          getCurrencySymbol(
            props.inventoryItems.find(item => item.id === props.selectedItems[0])
              ?.[props.bulkOps.priceUpdateTarget === 'sale' ? 'saleCurrency' : 'purchaseCurrency'] || 'TRY'
          ) : 'TL'}
        onPriceTypeChange={props.bulkOps.setPriceUpdateType}
        onPriceValueChange={props.bulkOps.setPriceUpdateValue}
        onConfirm={props.onBulkPriceUpdateConfirm}
        onCancel={props.onBulkPriceUpdateCancel}
      />

      <BulkDeleteImagesDialog
        open={props.bulkOps.showBulkDeleteImagesDialog}
        loading={props.bulkOps.bulkDeletingImages}
        selectedCount={props.selectedItems.length}
        onConfirm={props.onBulkDeleteImagesConfirm}
        onCancel={props.onBulkDeleteImagesCancel}
      />

      <BulkEcommerceDialog
        open={props.bulkOps.showBulkEcommerceDialog}
        loading={props.bulkOps.bulkEcommerceProcessing}
        selectedCount={props.selectedItems.length}
        action={props.bulkOps.bulkEcommerceAction}
        onConfirm={props.onBulkEcommerceConfirm}
        onCancel={props.onBulkEcommerceCancel}
      />

      <DeleteAllItemsDialog
        open={props.showDeleteAllDialog}
        onClose={props.onCancelDeleteAll}
        onConfirm={props.onConfirmDeleteAll}
        deleting={props.deletingAll}
        progress={props.deleteProgress}
        deletedCount={props.deletedItemsCount}
        totalCount={props.totalItemsToDelete}
        selectedWarehouse={props.selectedWarehouse}
      />

      <DeleteAllProductsDialog
        open={props.showDeleteAllProductsDialog}
        onClose={props.onCloseDeleteAllProducts}
        onConfirm={props.onConfirmDeleteAllProducts}
        counting={props.deleteAllCounting}
        productCount={props.deleteAllProductCount}
        categoryCount={props.deleteAllCategoryCount}
        deleting={props.deleteAllDeleting}
        phase={props.deleteAllPhase}
        processedCount={props.deleteAllProcessed}
        phaseTotal={props.deleteAllPhaseTotal}
        result={props.deleteAllResult}
      />

      <BulkKioskSendDialog
        open={props.bulkOps.showBulkKioskModal}
        onClose={props.onBulkToggleKioskCancel}
        onConfirm={props.onBulkToggleKioskConfirm}
        processing={props.bulkOps.bulkKioskProcessing}
        progress={props.bulkOps.bulkKioskProgress}
        results={props.bulkOps.bulkKioskResults}
        currentItem={props.bulkOps.bulkKioskCurrentItem}
      />

      <BulkQRMenuSendDialog
        open={props.bulkOps.showBulkQRMenuModal}
        onClose={props.onBulkToggleQRMenuCancel}
        onConfirm={props.onBulkToggleQRMenuConfirm}
        processing={props.bulkOps.bulkQRMenuProcessing}
        progress={props.bulkOps.bulkQRMenuProgress}
        results={props.bulkOps.bulkQRMenuResults}
        currentItem={props.bulkOps.bulkQRMenuCurrentItem}
      />

      <BulkWarehouseAssignDialog
        open={props.bulkOps.showBulkWarehouseAssignModal}
        onClose={props.onBulkAssignWarehouseCancel}
        onConfirm={props.onBulkAssignWarehouseConfirm}
        warehouses={props.warehouses}
        selectedCount={props.selectedItems.filter(itemId => {
          const item = props.inventoryItems.find(i => i.id === itemId);
          return item && (!item.warehouseStocks || item.warehouseStocks.length === 0);
        }).length}
        processing={props.bulkOps.bulkWarehouseAssignProcessing}
      />

      <BulkVatRateUpdateDialog
        open={props.bulkOps.showBulkVatRateDialog}
        loading={props.bulkOps.bulkVatRateUpdating}
        selectedCount={props.selectedItems.length}
        vatRate={props.bulkOps.bulkVatRate}
        onVatRateChange={props.bulkOps.setBulkVatRate}
        onConfirm={props.onBulkUpdateVatRateConfirm}
        onCancel={props.onBulkUpdateVatRateCancel}
      />

      <BulkDepartmentNoUpdateDialog
        open={props.bulkOps.showBulkDepartmentNoDialog}
        loading={props.bulkOps.bulkDepartmentNoUpdating}
        selectedCount={props.selectedItems.length}
        departmentNo={props.bulkOps.bulkDepartmentNo}
        onDepartmentNoChange={props.bulkOps.setBulkDepartmentNo}
        onConfirm={props.onBulkUpdateDepartmentNoConfirm}
        onCancel={props.onBulkUpdateDepartmentNoCancel}
      />

      <BulkVatAndDepartmentUpdateDialog
        open={props.bulkOps.showBulkVatAndDeptDialog}
        loading={props.bulkOps.bulkVatAndDeptUpdating}
        selectedCount={props.selectedItems.length}
        vatRate={props.bulkOps.bulkVatRate}
        departmentNo={props.bulkOps.bulkDepartmentNo}
        onVatRateChange={props.bulkOps.setBulkVatRate}
        onDepartmentNoChange={props.bulkOps.setBulkDepartmentNo}
        onConfirm={props.onBulkUpdateVatAndDeptConfirm}
        onCancel={props.onBulkUpdateVatAndDeptCancel}
      />

      <BulkActionSelectionModal
        open={props.isBulkActionSelectionModalOpen}
        onClose={props.onCloseBulkActionModal}
        onSelectAction={props.onSelectBulkAction}
        selectedWarehouse={props.selectedWarehouse}
      />

      {/* Menus */}
      <Menu
        anchorEl={props.menuAnchorEl}
        open={Boolean(props.menuAnchorEl)}
        onClose={handleCloseAllMenus}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {props.menuItemId && props.inventoryItems.find(item => item.id === props.menuItemId)?.isActive === false ? (
          <MenuItem onClick={props.onActivateItem}>
            <ListItemIcon>
              <CheckIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText 
              primary={t('stock.rowMenu.activate')} 
              secondary={t('stock.rowMenu.activateHint')}
            />
          </MenuItem>
        ) : (
          <>
            <MenuItem onClick={props.onMenuStockMovementsReport}>
              <ListItemIcon>
                <AssessmentIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary={t('stock.rowMenu.movementReport')} />
            </MenuItem>
            <MenuItem onClick={props.onMenuDetails}>
              <ListItemIcon>
                <InfoIcon fontSize="small" color="info" />
              </ListItemIcon>
              <ListItemText primary={t('stock.rowMenu.stockDetails')} />
            </MenuItem>
            <MenuItem onClick={props.onMenuStockMovement}>
              <ListItemIcon>
                <InventoryIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText primary={t('stock.rowMenu.stockMovement')} />
            </MenuItem>
            <MenuItem onClick={props.onOpenBranchPrice}>
              <ListItemIcon>
                <StoreIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText primary={t('stock.rowMenu.branchPrice')} />
            </MenuItem>
            <MenuItem onClick={props.onOpenRecipeModal}>
              <ListItemIcon>
                <RestaurantMenuIcon fontSize="small" sx={{ color: 'warning.main' }} />
              </ListItemIcon>
              <ListItemText primary={t('stock.rowMenu.simpleProduction')} />
            </MenuItem>
            <MenuItem onClick={props.onOpenMenuModal}>
              <ListItemIcon>
                <MenuBookIcon fontSize="small" sx={{ color: 'info.main' }} />
              </ListItemIcon>
              <ListItemText primary={t('stock.rowMenu.menuDefinitions')} />
            </MenuItem>
            <MenuItem onClick={props.onOpenVariantModal} disabled={!canManageVariants}>
              <ListItemIcon>
                <ViewModuleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText
                primary={t('stock.rowMenu.manageVariants')}
                secondary={!canManageVariants ? t('stock.rowMenu.mainProductsOnly') : undefined}
              />
            </MenuItem>
            <MenuItem onClick={props.onMenuEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary={t('stock.common.edit')} />
            </MenuItem>
            <MenuItem
              onMouseEnter={handleOpenPlatformMenu}
              onMouseLeave={handleClosePlatformMenuWithDelay}
            >
              <ListItemIcon>
                <StorefrontIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary={t('stock.rowMenu.openSalesChannels')} />
              <KeyboardArrowRightIcon fontSize="small" />
            </MenuItem>
            
            {/* Sil butonu - ayırıcı ile en altta */}
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={props.onMenuDelete}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary={t('stock.common.delete')} sx={{ color: 'error.main' }} />
            </MenuItem>
          </>
        )}
      </Menu>

      <Menu
        anchorEl={platformMenuAnchorEl}
        open={Boolean(platformMenuAnchorEl)}
        onClose={handleClosePlatformMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        MenuListProps={{
          onMouseEnter: () => {
            if (platformMenuCloseTimeoutRef.current) {
              clearTimeout(platformMenuCloseTimeoutRef.current);
              platformMenuCloseTimeoutRef.current = null;
            }
          },
          onMouseLeave: handleClosePlatformMenuWithDelay,
        }}
      >
        <MenuItem onClick={() => handlePlatformAction(props.onOpenForEcommerce)}>
          <ListItemIcon>
            <StorefrontIcon
              fontSize="small"
              color={props.menuItemId && props.ecommerceStatuses[props.menuItemId] ? "error" : "success"}
            />
          </ListItemIcon>
          <ListItemText
            primary={props.menuItemId && props.ecommerceStatuses[props.menuItemId] ? t('stock.rowMenu.closeEcommerce') : t('stock.rowMenu.openEcommerce')}
          />
        </MenuItem>
        <MenuItem onClick={() => handlePlatformAction(props.onSendToN11)}>
          <ListItemIcon>
            <CloudUploadIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('stock.rowMenu.sendToN11')} />
        </MenuItem>
        <MenuItem onClick={() => handlePlatformAction(props.onToggleQRMenu)}>
          <ListItemIcon>
            <StorefrontIcon
              fontSize="small"
              color={props.menuItemId && props.inventoryItems.find(item => item.id === props.menuItemId)?.isQRMenu ? "error" : "success"}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              props.menuItemId && props.inventoryItems.find(item => item.id === props.menuItemId)?.isQRMenu
                ? t('stock.rowMenu.closeQrMenu')
                : t('stock.rowMenu.openQrMenu')
            }
          />
        </MenuItem>
        <MenuItem onClick={() => handlePlatformAction(props.onToggleKiosk)}>
          <ListItemIcon>
            <StorefrontIcon
              fontSize="small"
              color={props.menuItemId && props.inventoryItems.find(item => item.id === props.menuItemId)?.isKiosk ? "error" : "info"}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              props.menuItemId && props.inventoryItems.find(item => item.id === props.menuItemId)?.isKiosk
                ? t('stock.rowMenu.closeKiosk')
                : t('stock.rowMenu.openKiosk')
            }
          />
        </MenuItem>
      </Menu>

      <FeaturesMenu
        anchorEl={props.anchorElFeatures}
        open={Boolean(props.anchorElFeatures)}
        onClose={props.onCloseFeaturesMenu}
        bulkActionMode={props.bulkActionMode}
        loading={props.loading}
        loadingExport={props.loadingExport}
        n11IntegrationActive={props.n11IntegrationActive}
        isMobile={props.isMobile}
        onOpenCategoryModal={props.onOpenCategoryModal}
        onOpenProductFeatureModal={props.onOpenProductFeatureModal}
        onOpenBrandModal={props.onOpenBrandModal}
        onOpenWarehouseModal={props.onOpenWarehouseModal}
        onStockMovementsReport={props.onStockMovementsReport}
        onToggleBulkActionMode={props.onToggleBulkActionMode}
        onNavigateToImageLibrary={props.onNavigateToImageLibrary}
        onOpenTableSettings={props.onOpenTableSettings}
        onOpenShipmentTemplates={props.onOpenShipmentTemplates}
        onExportToExcel={props.onExportToExcel}
        onOpenImportModal={props.onOpenImportModal}
        onOpenN11Upload={props.onOpenN11Upload}
        onOpenN11Log={props.onOpenN11Log}
        onOpenOtherActionsModal={props.onOpenOtherActionsModal}
        onPriceChangeLogs={props.onPriceChangeLogs}
        onLabelPrinting={props.onLabelPrinting}
        onScaleSync={props.onScaleSync}
        onProductRecipesPage={props.onProductRecipesPage}
        onProductMenusPage={props.onProductMenusPage}
        onStockCountPage={props.onStockCountPage}
      />

      <OtherActionsModal
        open={props.isOtherActionsModalOpen}
        onClose={props.onCloseOtherActionsModal}
        refreshing={props.refreshing}
        loading={props.loading}
        n11IntegrationActive={props.n11IntegrationActive}
        onNavigateToImageLibrary={props.onNavigateToImageLibraryFromOther}
        onOpenTableSettings={props.onOpenTableSettingsFromOther}
        onOpenShipmentTemplates={props.onOpenShipmentTemplatesFromOther}
        onOpenN11Log={props.onOpenN11LogFromOther}
        onOpenN11Upload={props.onOpenN11UploadFromOther}
        onRefreshInventory={props.onRefreshInventory}
      />

      {/* Snackbar */}
      <Snackbar
        open={props.snackbarOpen}
        autoHideDuration={4000}
        onClose={props.onCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={props.onCloseSnackbar} 
          severity={props.snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {props.snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

